// libs
import React, { useCallback, useMemo } from 'react';

// components
import { Divider } from '@lyearn/ui';

import { AvailableOptions } from '../../../types/schema';
import FilterGroupLabel from '../FilterGroupLabel';
import FilterItem from '../FilterItem';
// utils
import { isItemSelected } from '../utils';
import AVAILABLE_OPTION_COMPONENTS from './components/AvaialbleOptions';
// types
import { FilterGroupProps } from './types';

function FilterGroup(props: FilterGroupProps) {
  const {
    onMutate,
    onOptionClick,
    filterGroup,
    showDivider,
    itemRenderer,
    isGroupSelectionEnabled,
    addMultiple,
    removeMultiple,
    selectedItemsOfGroup,
    isSingleSelect,
    classes,
  } = props;
  const { label, options } = filterGroup;
  const isGroupSelected = useMemo(
    () => !options.some((option) => !isItemSelected(selectedItemsOfGroup, option)),
    [options, selectedItemsOfGroup],
  );
  const indeterminate = useMemo(
    () =>
      options.some((option) => isItemSelected(selectedItemsOfGroup, option)) && !isGroupSelected,
    [isGroupSelected, options, selectedItemsOfGroup],
  );

  const onGroupClick = useCallback(() => {
    if (indeterminate || isGroupSelected) {
      removeMultiple(options);
    } else {
      addMultiple(options);
    }
  }, [indeterminate, isGroupSelected, removeMultiple, options, addMultiple]);

  const renderAvailableOptions = useCallback(
    (availableOption: AvailableOptions) => {
      const Component = AVAILABLE_OPTION_COMPONENTS[availableOption?.type];
      if (Component) {
        return (
          <Component
            availableOption={availableOption}
            selectedItemsOfGroup={selectedItemsOfGroup}
            onMutate={onMutate}
          />
        );
      }
      return null;
    },
    [selectedItemsOfGroup, onMutate],
  );

  const renderFilterItem = useCallback(
    (option: any, _: any) => {
      const isSelected = isItemSelected(selectedItemsOfGroup, option);
      const isAvailableOptions = !!option?.availableOptions?.length;

      return (
        <>
          <FilterItem
            key={option.value}
            className={classes?.item}
            isSelected={isSelected}
            isSingleSelect={isSingleSelect}
            itemRenderer={itemRenderer}
            option={option}
            onClick={onOptionClick}
          />
          {isSelected && isAvailableOptions
            ? option?.availableOptions?.map(renderAvailableOptions)
            : null}
        </>
      );
    },
    [
      classes?.item,
      isSingleSelect,
      itemRenderer,
      onOptionClick,
      renderAvailableOptions,
      selectedItemsOfGroup,
    ],
  );

  return (
    <>
      <FilterGroupLabel
        className={classes?.group}
        indeterminate={indeterminate}
        isGroupSelectionEnabled={isGroupSelectionEnabled}
        isSelected={isGroupSelected}
        isSingleSelect={isSingleSelect}
        label={label}
        onClick={onGroupClick}
      />
      {options.map(renderFilterItem)}
      {showDivider ? <Divider className="my-8" /> : null}
    </>
  );
}

export default React.memo(FilterGroup);
